import React, { useRef, useEffect } from "react";
import { X, Bell, Check, Loader2, ExternalLink } from "lucide-react";
import { useNotification } from "../../contexts/NotificationContext";
import { cn } from "../../../lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Button } from "../../../components/ui/button";
import { useNavigate } from "react-router-dom";

export function NotificationSidebar() {
    const {
        isSidebarOpen,
        setIsSidebarOpen,
        notifications,
        isLoading,
        markAsRead,
        markAllAsRead,
        unreadCount
    } = useNotification();

    const sidebarRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                setIsSidebarOpen(false);
            }
        };
        if (isSidebarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSidebarOpen, setIsSidebarOpen]);

    const handleNotificationClick = (notification: any) => {
        if (!notification.isRead) {
            markAsRead(notification.id);
        }

        // Logic to navigate to related content
        if (notification.relatedContentId) {
            // This is a bit simplified, ideally we'd know the type of content
            // For now, let's try to find if it's a post
            navigate(`/dashboard?contentId=${notification.relatedContentId}`);
        }

        setIsSidebarOpen(false);
    };

    return (
        <>
            {/* Overlay */}
            {isSidebarOpen && (
                <div className="fixed inset-0 bg-black/40 z-[60] lg:z-[50]" />
            )}

            {/* Sidebar */}
            <div
                ref={sidebarRef}
                className={cn(
                    "fixed top-0 right-0 h-full w-80 sm:w-96 bg-content-bg border-l border-border shadow-2xl z-[70] transition-transform duration-300 ease-in-out transform",
                    isSidebarOpen ? "translate-x-0" : "translate-x-full"
                )}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-border">
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-primary" />
                            <h2 className="text-lg font-semibold text-content-text">Notifications</h2>
                            {unreadCount > 0 && (
                                <span className="bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs"
                                onClick={markAllAsRead}
                                disabled={unreadCount === 0}
                            >
                                <Check className="w-3.5 h-3.5 mr-1" />
                                Mark all read
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setIsSidebarOpen(false)}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-y-auto">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-40">
                                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                <p className="mt-2 text-sm text-muted-foreground">Loading notifications...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-60 px-6 text-center">
                                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                                    <Bell className="w-6 h-6 text-muted-foreground opacity-20" />
                                </div>
                                <h3 className="text-base font-medium text-content-text">No notifications yet</h3>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    We'll notify you when something important happens.
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={cn(
                                            "p-4 cursor-pointer hover:bg-hover transition-colors flex gap-3",
                                            !notification.isRead && "bg-primary/5"
                                        )}
                                    >
                                        <div className={cn(
                                            "mt-1 w-2 h-2 rounded-full flex-shrink-0",
                                            notification.isRead ? "bg-transparent" : "bg-primary"
                                        )} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={cn(
                                                    "text-sm font-medium text-content-text leading-tight mt-[-2px]",
                                                    !notification.isRead && "font-semibold"
                                                )}>
                                                    {notification.title}
                                                </p>
                                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                                    {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                </span>
                                            </div>
                                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                                                {notification.message}
                                            </p>
                                            {notification.relatedContentId && (
                                                <div className="mt-2 flex items-center text-[10px] text-primary font-medium">
                                                    <span>View details</span>
                                                    <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-border bg-muted/30">
                        <Button
                            variant="outline"
                            className="w-full text-xs h-9"
                            onClick={() => {
                                navigate('/account?tab=notifications');
                                setIsSidebarOpen(false);
                            }}
                        >
                            Notification Settings
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
